import { useLazyQuery } from '@apollo/client';
import {
	AutocompleteFilterDefinitionType,
	FilterMenuOutputType,
	SelectFilterDefinitionType,
	Table,
	TableOrderByType,
} from '@elipssolution/harfang';
import { Stack } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { FETCH_CANCELED_PROCEDURES, FetchCanceledProceduresType } from '../../../api/procedure';
import { FETCH_SIGNERS_IN_CANCELED_PROCEDURES, FetchSignersInCanceledProceduresType } from '../../../api/signer';
import ProcedureConsultationDialog from '../../../components/ProcedureConsultationDialog';
import { ProcedureType } from '../../../types/procedure';
import { SignerType } from '../../../types/signer';
import { baseProcedureColumns } from '../../../utils/procedureBaseColumns';
import { getSignerFilter } from '../../../utils/renderSigner';

type ProcedureFilterType = {
	isPrivate: SelectFilterDefinitionType<boolean>;
	signer: AutocompleteFilterDefinitionType<SignerType>;
};

const CanceledProceduresTable = () => {
	const [procedureConsultationId, setProcedureConsultationId] = useState<ProcedureType['id']>();

	const [fetchCanceledProcedures] = useLazyQuery<FetchCanceledProceduresType>(FETCH_CANCELED_PROCEDURES);
	const [fetchSignersInCanceledProcedures] = useLazyQuery<FetchSignersInCanceledProceduresType>(
		FETCH_SIGNERS_IN_CANCELED_PROCEDURES,
	);

	const proceduresDataSource = useCallback(
		async (
			limit: number,
			offset: number,
			search?: string,
			orderBy?: TableOrderByType<ProcedureType>,
			filters?: FilterMenuOutputType<ProcedureFilterType>,
		): Promise<{
			count: number;
			items: ProcedureType[];
		}> => {
			const { field, order } = orderBy || {};
			const { signer, ...restFilters } = filters ?? {};

			const { data, error } = await fetchCanceledProcedures({
				variables: {
					...(!!filters && {
						filter: {
							...restFilters,
							...(signer && {
								signerFirstName: { eq: signer.eq?.firstName },
								signerLastName: { eq: signer.eq?.lastName },
							}),
						},
					}),
					...(orderBy && { orderBy: { field, order } }),
					page: {
						limit,
						offset,
					},
					search,
				},
			});

			if (error) {
				throw error;
			}

			const {
				sign_canceledProcedures: { count = 0, items = [] },
			} = data ?? {
				sign_canceledProcedures: {},
			};

			return {
				count,
				items: items.map(({ updatedAt, ...rest }) => ({
					...rest,
					updatedAt: new Date(updatedAt),
				})),
			};
		},
		[fetchCanceledProcedures],
	);

	const signersInCanceledProceduresDataSource = useCallback(
		async (
			limit: number,
			offset: number,
			search?: string,
		): Promise<{
			count: number;
			items: SignerType[];
		}> => {
			const { data, error } = await fetchSignersInCanceledProcedures({
				variables: {
					page: {
						limit,
						offset,
					},
					search,
				},
			});

			if (error) {
				throw error;
			}

			const {
				sign_signersInCanceledProcedures: { count = 0, items = [] },
			} = data ?? {
				sign_signersInCanceledProcedures: {},
			};

			return { count, items: items.map((signer) => ({ ...signer, id: uuid() })) };
		},
		[fetchSignersInCanceledProcedures],
	);

	const filters: ProcedureFilterType = useMemo(
		() => ({
			isPrivate: {
				label: 'Visibilité',
				type: 'select',
				options: [true, false],
				renderOption: (value) => (value ? 'Privée' : 'Publique'),
				renderValue: (value) => (value ? 'Privée' : 'Publique'),
			},
			signer: getSignerFilter(signersInCanceledProceduresDataSource),
		}),
		[signersInCanceledProceduresDataSource],
	);

	const handleRowClick = useCallback(
		({ id: selectedProcedureId }: ProcedureType) => setProcedureConsultationId(selectedProcedureId),
		[],
	);

	const handleProcedureViewDialogClose = useCallback(() => setProcedureConsultationId(undefined), []);

	return (
		<Stack height="100%" width="100%" flex={3}>
			<Table<ProcedureType, ProcedureFilterType>
				columns={baseProcedureColumns}
				dataSource={proceduresDataSource}
				filters={filters}
				onRowClick={handleRowClick}
				title="Procédures annulées"
				enableSearch
			/>

			<ProcedureConsultationDialog procedureId={procedureConsultationId} onClose={handleProcedureViewDialogClose} />
		</Stack>
	);
};

export default CanceledProceduresTable;
